import * as dom from '@front/dom';
import { requestAnimationFramePromise } from '@shared/requestanimationframepromise';
import { Peeky } from '@front/peeky';
import { PeekyEmitter } from '@shared/peeky.emitter';
import { compareStates, pxFromVw } from './helpers';

import slideshowGridder from './grid.slideshow';
import flowGridder from './grid.flow';
import dynamicGridder from './grid.dynamic';
import fixedGridder from './grid.fixed';


export type GridItemDimensions = {
  width?: number,
  height?: number
};

export type Gridder = (items: HTMLElement[], state: GridState, previousState?: GridState) => GridLayout;

export type GriddedItem = {
  DOM: HTMLElement;
  css: {
    top?: number,
    left?: number,
    width?: number,
    height?: number
  };
}

export type GridLayout = {
  translateX?: number;
  translateY?: number;
  width?: number | '';
  height?: number | '';
  itemList?: GriddedItem[];
}

export type RenderOptions = {
  animate?: boolean;
}

export type GridOptions = {
  paneClassName: string;
  itemClassName: string;
  mobileMaxWidth: number;
  tabletMaxWidth: number;
};

export interface GridState {
  tabletGridSpacingX: number;
  tabletGridSpacingY: number;
  tabletGridColumns: number;
  tabletWidthHeightRatio: number;
  tabletGridSlideWidthHeightRatioMode: string;
  tabletGridSlideWidthHeightRatio: number;
  tabletContainerMarginTop: number;
  tabletContainerMarginBottom: number;
  tabletContainerMarginLeft: number;
  tabletContainerMarginRight: number;
  tabletGridder: string | boolean;

  mobileGridSpacingX: number;
  mobileGridSpacingY: number;
  mobileGridColumns: number;
  mobileWidthHeightRatio: number;
  mobileGridSlideWidthHeightRatioMode: string;
  mobileGridSlideWidthHeightRatio: number;
  mobileContainerMarginTop: number;
  mobileContainerMarginBottom: number;
  mobileContainerMarginLeft: number;
  mobileContainerMarginRight: number;
  mobileGridder: string | boolean;

  desktopGridSpacingX: number;
  desktopGridSpacingY: number;
  desktopGridColumns: number;
  desktopWidthHeightRatio: number;
  desktopGridSlideWidthHeightRatioMode: string;
  desktopGridSlideWidthHeightRatio: number;
  desktopContainerMarginTop: number;
  desktopContainerMarginBottom: number;
  desktopContainerMarginLeft: number;
  desktopContainerMarginRight: number;
  desktopGridder: string | boolean;

  // The following variables are calculated automatically based on the current responsive mode
  gridSpacingX?: number;
  gridSpacingY?: number;
  gridColumns?: number;
  widthHeightRatio?: number;
  gridSlideWidthHeightRatioMode?: string;
  gridSlideWidthHeightRatio?: number;
  containerMarginTop?: number;
  containerMarginBottom?: number;
  containerMarginLeft?: number;
  containerMarginRight?: number;
  containerWidth?: number;
  containerHeight?: number;
  gridder?: string | boolean;

  startNo: number;
  /** The "mode" of a slideshow grid, single or multi. */
  gridSlideMode: string;

  responsiveMode?: string;
  /** gridSpacingX converted to px units */
  gridSpacingXPx?: number;
  /** gridSpacingY converted to px units */
  gridSpacingYPx?: number;
  /** Specify if all items should be prioritized visually */
  allpri?: boolean;
}



export class Grid extends PeekyEmitter {
  
  pk: Peeky;
  DOM: HTMLElement;
  pane: HTMLElement;
  items: HTMLElement[];
  _ready: boolean = false;
  _currentLayout: GridLayout;
  _options: GridOptions = {
    paneClassName: '.gridPane',
    itemClassName: '.gridItem',
    mobileMaxWidth: 450,
    tabletMaxWidth: 1024
  };

  // Rendered state may differ from current state, since render() accepts an additional state mutation
  _renderedState?: GridState;
  _pendingRenderState?: GridState;
  _state: GridState = {
    tabletGridSpacingX: 1,
    tabletGridSpacingY: 1,
    tabletGridColumns: 4,
    tabletWidthHeightRatio: 1.8,
    tabletGridSlideWidthHeightRatioMode: 'custom',
    tabletGridSlideWidthHeightRatio: 1.8,
    tabletContainerMarginTop: 0,
    tabletContainerMarginBottom: 0,
    tabletContainerMarginLeft: 0,
    tabletContainerMarginRight: 0,
  
    mobileGridSpacingX: 1,
    mobileGridSpacingY: 1,
    mobileGridColumns: 1,
    mobileWidthHeightRatio: 1.8,
    mobileGridSlideWidthHeightRatioMode: 'custom',
    mobileGridSlideWidthHeightRatio: 1.8,
    mobileContainerMarginTop: 0,
    mobileContainerMarginBottom: 0,
    mobileContainerMarginLeft: 0,
    mobileContainerMarginRight: 0,
  
    desktopGridSpacingX: 1,
    desktopGridSpacingY: 1,
    desktopGridColumns: 4,
    desktopWidthHeightRatio: 1.8,
    desktopGridSlideWidthHeightRatioMode: 'custom',
    desktopGridSlideWidthHeightRatio: 1.8,
    desktopContainerMarginTop: 0,
    desktopContainerMarginBottom: 0,
    desktopContainerMarginLeft: 0,
    desktopContainerMarginRight: 0,
  
    startNo: 0,
    gridSlideMode: 'single',  
    desktopGridder: '',
    tabletGridder: '',
    mobileGridder: ''
  };
  _needsReRender: boolean = true;


  constructor(pk: Peeky, grid: HTMLElement, _options: Partial<GridOptions> = {}) {
    super();
    this.pk = pk;
    this.DOM = grid;

    this._options = {
      ...this._options,
      ..._options
    };
    this.pane = grid.querySelector(this._options.paneClassName);

    this.reload();
    this.resize();

    this.one('rendered', () => {
      this._ready = true;
      this.emit('ready');
    });
  }


  /**
   * Update this.items with list of grid items
   */
  updateItems(): void {
    let nodeListItems: NodeListOf<HTMLElement> = this.DOM.querySelectorAll(this._options.itemClassName);
    let items = Array.from(nodeListItems);
    this.items = items;
    for (let i=0; i<items.length; i++) {
      let item = items[i];
      let currentRandomClass = item.dataset.rnd;
      if (currentRandomClass) {
        return;
      }
      let newRandom5Class = 'rnd-' + ((Math.round(Math.random()*items.length) % 10)+1) + '-10';
      item.classList.add(newRandom5Class);
      item.dataset.rnd = newRandom5Class;
    }
  }


  async ready() {
    if (this._ready) {
      return;
    } else {
      await new Promise<void>((resolve) => {
        this.one('ready', () => {
          resolve();
        });
      });
    }
  }


  /**
   * Parse DOM data attributes, and update grid item list
   */
  reload() {
    let state = this.parseDomData();
    let updated = this.mutateState(state);
    this.updateItems();
    if (updated) {
      this.render();
    }
  }


  resize() {
    let DOM = this.DOM;
    let winWidth = window.innerWidth;
    let dimensions = dom.getDimensions(DOM);
    let responsiveMode: string;
    let style = window.getComputedStyle(DOM);

    if (winWidth <= this._options.mobileMaxWidth) {
      responsiveMode = 'mobile';
    } else if (winWidth < this._options.tabletMaxWidth) {
      responsiveMode = 'tablet';
    } else  {
      responsiveMode = 'desktop';
    }

    let updated = this.mutateState({
      containerWidth: dimensions.width,
      containerHeight: dimensions.height,
      responsiveMode: responsiveMode,
      containerMarginLeft: parseFloat(style.marginLeft || '0'),
      containerMarginTop: parseFloat(style.marginTop || '0'),
      containerMarginRight: parseFloat(style.marginRight || '0'),
      containerMarginBottom: parseFloat(style.marginBottom || '0')
    });
    if (this._needsReRender || updated) {
      this.render();
      this._needsReRender = false;
    }
  }


  /**
   * Look for state variables in container DOM data-attributes
   * @returns state mutation containing values from DOM
   */
  parseDomData(): Partial<GridState> {
    let state: Partial<GridState> = {};
    let DOM = this.DOM;

    if (typeof(dom.data(DOM, 'tablet-gridspacingx')) !== 'undefined') state.tabletGridSpacingX = parseFloat(dom.data(DOM, 'tablet-gridspacingx'));
    if (typeof(dom.data(DOM, 'tablet-gridspacingy')) !== 'undefined') state.tabletGridSpacingY = parseFloat(dom.data(DOM, 'tablet-gridspacingy'));
    if (typeof(dom.data(DOM, 'tablet-gridcolumns')) !== 'undefined') state.tabletGridColumns = parseFloat(dom.data(DOM, 'tablet-gridcolumns'));
    if (typeof(dom.data(DOM, 'tablet-widthheightratio')) !== 'undefined') state.tabletWidthHeightRatio = parseFloat(dom.data(DOM, 'tablet-widthheightratio'));
    if (typeof(dom.data(DOM, 'tablet-gridslidewidthheightratiomode')) !== 'undefined') state.tabletGridSlideWidthHeightRatioMode = dom.data(DOM, 'tablet-gridslidewidthheightratiomode');
    if (typeof(dom.data(DOM, 'tablet-gridslidewidthheightratio')) !== 'undefined') state.tabletGridSlideWidthHeightRatio = parseFloat(dom.data(DOM, 'tablet-gridslidewidthheightratio'));

    if (typeof(dom.data(DOM, 'mobile-gridspacingx')) !== 'undefined') state.mobileGridSpacingX = parseFloat(dom.data(DOM, 'mobile-gridspacingx'));
    if (typeof(dom.data(DOM, 'mobile-gridspacingy')) !== 'undefined') state.mobileGridSpacingY = parseFloat(dom.data(DOM, 'mobile-gridspacingy'));
    if (typeof(dom.data(DOM, 'mobile-gridcolumns')) !== 'undefined') state.mobileGridColumns = parseFloat(dom.data(DOM, 'mobile-gridcolumns'));
    if (typeof(dom.data(DOM, 'mobile-widthheightratio')) !== 'undefined') state.mobileWidthHeightRatio = parseFloat(dom.data(DOM, 'mobile-widthheightratio'));
    if (typeof(dom.data(DOM, 'mobile-gridslidewidthheightratiomode')) !== 'undefined') state.mobileGridSlideWidthHeightRatioMode = dom.data(DOM, 'mobile-gridslidewidthheightratiomode');
    if (typeof(dom.data(DOM, 'mobile-gridslidewidthheightratio')) !== 'undefined') state.mobileGridSlideWidthHeightRatio = parseFloat(dom.data(DOM, 'mobile-gridslidewidthheightratio'));

    if (typeof(dom.data(DOM, 'gridspacingx')) !== 'undefined') state.desktopGridSpacingX = parseFloat(dom.data(DOM, 'gridspacingx'));
    if (typeof(dom.data(DOM, 'gridspacingy')) !== 'undefined') state.desktopGridSpacingY = parseFloat(dom.data(DOM, 'gridspacingy'));
    if (typeof(dom.data(DOM, 'gridcolumns')) !== 'undefined') state.desktopGridColumns = parseFloat(dom.data(DOM, 'gridcolumns'));
    if (typeof(dom.data(DOM, 'widthheightratio')) !== 'undefined') state.desktopWidthHeightRatio = parseFloat(dom.data(DOM, 'widthheightratio'));
    if (typeof(dom.data(DOM, 'gridslidewidthheightratiomode')) !== 'undefined') state.desktopGridSlideWidthHeightRatioMode = dom.data(DOM, 'gridslidewidthheightratiomode');
    if (typeof(dom.data(DOM, 'gridslidewidthheightratio')) !== 'undefined') state.desktopGridSlideWidthHeightRatio = parseFloat(dom.data(DOM, 'gridslidewidthheightratio'));

    if (typeof(dom.data(DOM, 'grid-startno')) !== 'undefined') state.startNo = parseFloat(dom.data(DOM, 'grid-startno'));
    if (typeof(dom.data(DOM, 'gridslidemode')) !== 'undefined') state.gridSlideMode = dom.data(DOM, 'gridslidemode');
    if (typeof(dom.data(DOM, 'gridgridder')) !== 'undefined') state.gridder = dom.data(DOM, 'gridgridder');
    if (typeof(dom.data(DOM, 'desktop-gridder')) !== 'undefined') state.desktopGridder = dom.data(DOM, 'desktop-gridder');
    if (typeof(dom.data(DOM, 'tablet-gridder')) !== 'undefined') state.tabletGridder = dom.data(DOM, 'tablet-gridder');
    if (typeof(dom.data(DOM, 'mobile-gridder')) !== 'undefined') state.mobileGridder = dom.data(DOM, 'mobile-gridder');

    // Do nothing if gridder is given as data
    if (state.gridder) {
    } else if (DOM.classList.contains('flow')) {
      state.gridder = 'flow';
    } else if (DOM.classList.contains('mason')) {
      state.gridder = 'mason';
    } else if (DOM.classList.contains('plain')) {
      state.gridder = 'plain';
    } else if (DOM.classList.contains('slideshow')) {
      state.gridder = 'slideshow';
    }

    return state;
  }


  /**
   * @param state 
   * @returns true if updated, false if not
   */
  mutateState(state: Partial<GridState> = {}): boolean {
    let newState = {
      ...this._state,
      ...state
    };
    if (compareStates(this._state, newState)) {
      return false;
    } else {
      this._state = newState;
      return true;
    }
  }


  /**
   * Reset DOM to how it was before gridding. Reset style properties, and remove classes.
   */
  reset(): void {
    let items = this.items || [];
    for (let i=0; i<items.length; i++) {
      let item = items[i];
      item.style.position = '';
      item.style.width = '';
      item.style.height = '';
      item.style.top = '';
      item.style.left = '';
      item.style.transform = '';
      item.classList.remove('gridded');
    }
    this.DOM.style.height = '';
    let pane = this.pane;
    if (pane) {
      pane.style.height = '';
      pane.style.width = '';
      pane.style.transform = '';
      pane.style.left = '';
      pane.style.top = '';
    }
    let currentGridder = this._renderedState?.gridder;
    if (typeof(currentGridder) === 'string') {
      this.DOM.classList.remove(currentGridder);
    }
    this._renderedState = undefined;
    this._needsReRender = true;

    this._ready = false;
    this.one('rendered', () => {
      this._ready = true;
      this.emit('ready');
    })
  }

  /**
   * 
   * @param stateMutations Mutations for this render only, will not update state
   * @param renderOptions 
   */
  async render(stateMutations: Partial<GridState> = {}, renderOptions: RenderOptions = {}): Promise<void> {
    let previousState = this._state;
    let renderState: GridState = {
      ...this._state,
      ...stateMutations
    };

    // Look for variables for current responsiveMode, use those variables
    // e.g. tabletGridSpacingX: 1 -> gridSpacingX: 1
    let properties = [
      'Gridder',
      'GridSpacingX',
      'GridSpacingY',
      'GridColumns',
      'WidthHeightRatio',
      'GridSlideWidthHeightRatioMode',
      'GridSlideWidthHeightRatio'
    ];
    for (let i=0; i<properties.length; i++) {
      let name = properties[i];
      var lcname = name.charAt(0).toLowerCase() + name.slice(1);
      renderState[lcname] = renderState[renderState.responsiveMode + name] || renderState[lcname] || renderState['desktop' + name];
    }

    // If another render of the same state is pending, don't keep going
    if (this._pendingRenderState && compareStates(this._pendingRenderState, renderState)) {
      await new Promise((resolve) => {
        this.once('rendered', resolve);
      });
      return;
    }

    // Delay render, to avoid multiple successive calls (e.g. from resize)
    this._pendingRenderState = renderState;
    await requestAnimationFramePromise(2);

    // If pending state is updated, render() of another state has been called while waiting
    if (!compareStates(this._pendingRenderState, renderState)) {
      await new Promise((resolve) => {
        this.once('rendered', resolve);
      });
      return;
    }

    // Clean up after waiting
    this._pendingRenderState = undefined;

    let grid = this;
    let DOM = this.DOM;
    let items = this.items;

    // Grid is disabled, reset and do nothing
    if (!renderState.gridder || typeof(renderState.gridder) !== 'string') {
      this.reset();
      return;
    }

    renderState.gridSpacingXPx = pxFromVw(renderState.gridSpacingX);
    renderState.gridSpacingYPx = pxFromVw(renderState.gridSpacingY);

    // Set all pri for mobile / tablet by default
    if (typeof(renderState.allpri) === 'undefined' && (renderState.responsiveMode === 'tablet' || renderState.responsiveMode === 'mobile')) {
      renderState.allpri = true;
    }

    let currentGridder = this._renderedState?.gridder;
    let newGridder = renderState.gridder;

    if (!renderOptions.animate && (!this._renderedState || currentGridder === newGridder)) {
      renderOptions.animate = false;
    }

    // Check if resize is already happening
    // If it's happening and we have new sizes, cancel the previous one.
    if (this._renderedState && compareStates(this._renderedState, renderState)) {
      return;
    } else {
      this._renderedState = renderState;
    }

    // Reset size to get content size for grid types
    if (renderOptions.animate !== false) {
      this.pk.emit('resize', { type: 'gridResize' });
    }

    // Add new gridder class
    if (newGridder !== currentGridder) {
      DOM.classList.add(newGridder);
      if (typeof(currentGridder) === 'string') {
        DOM.classList.remove(currentGridder);
      }
    }

    // Create grid object that will calculate positions
    let gridder: Gridder;
    switch (newGridder) {
      case 'slideshow': gridder = slideshowGridder; break;
      case 'flow': gridder = flowGridder; break;
      case 'mason': gridder = dynamicGridder; break;
      default: gridder = fixedGridder;
    }
    dom.prepareDimensions(DOM);
    let result: GridLayout = gridder(items, renderState, previousState);
    let currentPosition: GridLayout = grid._currentLayout || {};
    dom.restoreDimensions(DOM);

    let transitionPromises = [];

    // Animate pane width/height (unless disabled)
    if (renderOptions.animate !== false) {
      let pane = grid.pane;
      pane.style.transform = 'translateX(' + (currentPosition.translateX || 0) + 'px) translateY(' + (currentPosition.translateY || 0) + 'px) translateZ(0)';
      pane.style.transition = 'all 0s';
      pane.style.left = '';
      pane.style.top = '';
      pane.style.width = result.width ? result.width + 'px' : renderState.containerWidth ? renderState.containerWidth + 'px' : '';
      pane.style.height = result.height ? result.height + 'px' : '';

      // Make sure style is updated
      await requestAnimationFramePromise(2);

      pane.style.transform = 'translateX(' + (result.translateX || 0) + 'px) translateY(' + (result.translateY || 0) + 'px) translateZ(0)';
      pane.style.transition = '';
      DOM.style.transition = 'height .35s';

      transitionPromises.push(dom.transitionend(pane, 'transform'));
    }
    DOM.style.height = result.height ? result.height + 'px' : '';

    for (let i=0; i<result.itemList.length; i++) {
      let item = result.itemList[i];
      // Set transition property if we're animating
      if (renderOptions.animate !== false) {
        item.DOM.classList.add('gridTransition');
        item.DOM.style.transition = 'all .25s';
      }
      item.DOM.style.position = 'absolute';
      for (let key in item.css) {
        item.DOM.style[key] = item.css[key] + 'px';
      }
      // Wait for transitionend if we're animating
      if (renderOptions.animate !== false) {
        transitionPromises.push(new Promise<void>(async (resolve) => {
          await dom.transitionend(item.DOM);
          item.DOM.classList.remove('gridTransition');
          item.DOM.style.transition = '';
          resolve();
        }));
      }
      item.DOM.classList.add('gridded');
    }

    grid._currentLayout = result;
    await Promise.all(transitionPromises);

    // When done, change from translateXY to left/top
    let pane = grid.pane;
    pane.style.transform = '';
    pane.style.transition = 'all 0s';
    pane.style.left = grid._currentLayout.translateX ? grid._currentLayout.translateX + 'px' : '';
    pane.style.top = grid._currentLayout.translateY ? grid._currentLayout.translateY + 'px' : '';
    pane.style.width = grid._currentLayout.width ? grid._currentLayout.width + 'px' : '';
    pane.style.height = grid._currentLayout.height ? grid._currentLayout.height + 'px' : '';

    await requestAnimationFramePromise(2);

    pane.style.transition = '';

    this.emit('rendered', renderState);
    this.pk.emit('resize', { type: 'gridResize' });
  }
};


